import React from "react";
import {
  Grid,
  TextField,
  Autocomplete,
  Paper,
  Typography,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
} from "@mui/material";
import { Controller, Control, UseFormSetValue } from "react-hook-form";

interface RulesSectionProps {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  viewMode: boolean;
  masterData: any;
  states: any[];
  districts: any[];
  choicesMasterData: any;
  isLocationDisabled: boolean;
  genderOptions: any[];
}

export const RulesSection: React.FC<RulesSectionProps> = ({
  control,
  setValue,
  viewMode,
  masterData,
  states,
  districts,
  choicesMasterData,
  isLocationDisabled,
  genderOptions,
}) => {
  districts=[{id:'All',label:'All',value:'All'},...districts];
  const workExperienceOptions = [
    { id: "All", label: "All", value: "All" },
    ...(
      choicesMasterData?.workExperience?.filter(
        (item: any) => !item?.isAggregate && !item?.is_aggregate
      ) || []
    ),
  ];
  return (
    <Paper sx={{ p: 3 }} elevation={1}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: "#91278F" }}>
        Rules
      </Typography>
      <Grid container spacing={2}>
        {/* Min Age */}
        <Grid item xs={12} md={6}>
          <Controller
            name="min_age"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                type="number"
                label="Min Age"
                disabled={viewMode}
                value={field.value || ""}
                onChange={(e) => {
                  const value = e.target.value ? parseInt(e.target.value) : null;
                  field.onChange(value);
                }}
              />
            )}
          />
        </Grid>

        {/* Max Age */}
        <Grid item xs={12} md={6}>
          <Controller
            name="max_age"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                type="number"
                label="Max Age"
                disabled={viewMode}
                value={field.value || ""}
                onChange={(e) => {
                  const value = e.target.value ? parseInt(e.target.value) : null;
                  field.onChange(value);
                }}
              />
            )}
          />
        </Grid>

        {/* Gender */}
        <Grid item xs={12} md={6}>
          <Controller
            name="gender"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                fullWidth
                options={genderOptions}
                getOptionLabel={(option) => option.value || ""}
                value={
                  genderOptions.find(
                    (g: any) => g.id === field.value || g.value === field.value
                  ) || null
                }
                onChange={(_, newValue) => {
                  field.onChange(newValue?.id || null);
                }}
                disabled={viewMode}
                renderInput={(params) => (
                  <TextField {...params} label="Gender" />
                )}
              />
            )}
          />
        </Grid>

        {/* Occupation */}
        <Grid item xs={12} md={6}>
          <Controller
            name="occupation"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                fullWidth
                options={
                  masterData?.occupation?.filter(
                    (item: any) => !item?.isAggregate
                  ) || []
                }
                getOptionLabel={(option: any) => option?.value || ""}
                value={
                  masterData?.occupation?.find(
                    (item: any) => item.id === field.value
                  ) || null
                }
                onChange={(_, newValue: any) => {
                  field.onChange(newValue?.id || null);
                }}
                disabled={viewMode}
                renderInput={(params) => (
                  <TextField {...params} label="Occupation" />
                )}
              />
            )}
          />
        </Grid>

        {/* State */}
        <Grid item xs={12} md={6}>
          <Controller
            name="state"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                fullWidth
                options={states || []}
                getOptionLabel={(option: any) => option?.value || ""}
                value={states?.find((item: any) => item.id === field.value) || null}
                onChange={(_, newValue: any) => {
                  field.onChange(newValue?.id || null);
                  setValue("district", null);
                }}
                disabled={viewMode || isLocationDisabled}
                renderInput={(params) => (
                  <TextField {...params} label="State" />
                )}
              />
            )}
          />
        </Grid>

        {/* District */}
        <Grid item xs={12} md={6}>
          <Controller
            name="district"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                fullWidth
                options={districts}
                getOptionLabel={(option: any) => option?.value || ''}
                value={
                  districts?.find((item: any) => item.id === field.value) || null
                }
                onChange={(_, newValue: any) => {                  
                  field.onChange(newValue?.id || null);
                }}
                disabled={viewMode || isLocationDisabled}
                renderInput={(params) => (
                  <TextField {...params} label="District" />
                )}
              />
            )
            }
          />
        </Grid>

        {/* Locality */}
        <Grid item xs={12} md={6}>
          <Controller
            name="locality"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Locality"
                disabled={viewMode || isLocationDisabled}
              />
            )}
          />
        </Grid>

        {/* Pincode */}
        <Grid item xs={12} md={6}>
          <Controller
            name="pincode"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Pincode"
                disabled={viewMode}
                inputProps={{ maxLength: 6 }}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, "");
                  field.onChange(value);
                }}
              />
            )}
          />
        </Grid>

        {/* Areas of Interest */}
        <Grid item xs={12} md={6}>
          <Controller
            name="areas_of_interest"
            control={control}
            render={({ field }) => {
              const areasOfInterestOptions =
                choicesMasterData?.areasOfInterest?.filter(
                  (item: any) => !item?.isAggregate && !item?.is_aggregate
                ) || [];

              const selectedValues = field.value || [];
              const currentValue = areasOfInterestOptions.filter((item: any) =>
                selectedValues.includes(item.id)
              );

              return (
                <Autocomplete
                  fullWidth
                  multiple
                  options={areasOfInterestOptions}
                  getOptionLabel={(option: any) => option?.value || ""}
                  value={currentValue}
                  onChange={(_, newValue: any[]) => {
                    field.onChange(newValue.map((item: any) => item.id));
                  }}
                  disabled={viewMode}
                  renderInput={(params) => (
                    <TextField {...params} label="Areas of Interest" />
                  )}
                  isOptionEqualToValue={(option: any, value: any) =>
                    option.id === value.id
                  }
                />
              );
            }}
          />
        </Grid>

        {/* Area of Residence */}
        <Grid item xs={12} md={6}>
          <Controller
            name="area_of_residence"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                fullWidth
                options={
                  masterData?.areaOfResidence?.filter(
                    (item: any) => !item?.isAggregate
                  ) || []
                }
                getOptionLabel={(option: any) => option?.value || ""}
                value={
                  masterData?.areaOfResidence?.find(
                    (item: any) => item.id === field.value
                  ) || null
                }
                onChange={(_, newValue: any) => {
                  field.onChange(newValue?.id || null);
                }}
                disabled={viewMode}
                renderInput={(params) => (
                  <TextField {...params} label="Area of Residence" />
                )}
              />
            )}
          />
        </Grid>

        {/* Caste */}
        <Grid item xs={12} md={6}>
          <Controller
            name="caste"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                fullWidth
                options={
                  masterData?.caste?.filter((item: any) => !item?.isAggregate) ||
                  []
                }
                getOptionLabel={(option: any) => option?.value || ""}
                value={
                  masterData?.caste?.find((item: any) => item.id === field.value) ||
                  null
                }
                onChange={(_, newValue: any) => {
                  field.onChange(newValue?.id || null);
                }}
                disabled={viewMode}
                renderInput={(params) => (
                  <TextField {...params} label="Caste" />
                )}
              />
            )}
          />
        </Grid>

        {/* Working Status */}
        <Grid item xs={12} md={6}>
          <Controller
            name="working_status"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                fullWidth
                options={
                  masterData?.workingStatus?.filter(
                    (item: any) => !item?.isAggregate
                  ) || []
                }
                getOptionLabel={(option: any) => option?.value || ""}
                value={
                  masterData?.workingStatus?.find(
                    (item: any) => item.id === field.value
                  ) || null
                }
                onChange={(_, newValue: any) => {
                  field.onChange(newValue?.id || null);
                }}
                disabled={viewMode}
                renderInput={(params) => (
                  <TextField {...params} label="Working Status" />
                )}
              />
            )}
          />
        </Grid>

        {/* Family Income */}
        <Grid item xs={12} md={6}>
          <Controller
            name="family_income"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                fullWidth
                options={
                  masterData?.familyIncome?.filter(
                    (item: any) => !item?.isAggregate
                  ) || []
                }
                getOptionLabel={(option: any) => option?.value || ""}
                value={
                  masterData?.familyIncome?.find(
                    (item: any) => item.id === field.value
                  ) || null
                }
                onChange={(_, newValue: any) => {
                  field.onChange(newValue?.id || null);
                }}
                disabled={viewMode}
                renderInput={(params) => (
                  <TextField {...params} label="Family Income" />
                )}
              />
            )}
          />
        </Grid>

        {/* Education Qualification */}
        <Grid item xs={12} md={6}>
          <Controller
            name="education_qualification"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                fullWidth
                options={
                  masterData?.education?.filter(
                    (item: any) => !item?.isAggregate
                  ) || []
                }
                getOptionLabel={(option: any) => option?.value || ""}
                value={
                  masterData?.education?.find(
                    (item: any) => item.id === field.value
                  ) || null
                }
                onChange={(_, newValue: any) => {
                  field.onChange(newValue?.id || null);
                }}
                disabled={viewMode}
                renderInput={(params) => (
                  <TextField {...params} label="Education Qualification" />
                )}
              />
            )}
          />
        </Grid>

        {/* Work Experience */}
        <Grid item xs={12} md={6}>
          <Controller
            name="work_experience"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                fullWidth
                options={workExperienceOptions}
                getOptionLabel={(option: any) => option?.value || option?.label || ""}
                value={
                  workExperienceOptions.find((item: any) => item.id === field.value) ||
                  null
                }
                onChange={(_, newValue: any) => {
                  field.onChange(newValue?.id ?? null);
                }}
                disabled={viewMode}
                renderInput={(params) => (
                  <TextField {...params} label="Work Experience" />
                )}
              />
            )}
          />
        </Grid>

        {/* Differently Abled */}
        <Grid item xs={12} md={12}>
          <div style={{display:'flex', alignItems:'center', gap:'1rem'}}>
            <Typography width={'20%'}>Differently Abled : </Typography>
            <Controller
              name="differently_abled"
              control={control}
              render={({ field }) => (
                <RadioGroup 
                  row 
                  value={field.value ?? ''} 
                  onChange={(e, value) => field.onChange(value)}
                >
                  <FormControlLabel
                    label='Yes'
                    value={true}
                    control={
                      <Radio disabled={viewMode} />
                    }
                  />
                  <FormControlLabel
                    label='No'
                    value={false}
                    control={
                      <Radio disabled={viewMode} />
                    }
                  />
                  <FormControlLabel
                    label='All'
                    value={'all'}
                    control={
                      <Radio disabled={viewMode} />
                    }
                  />
                </RadioGroup>
              )}
            />
          </div>
        </Grid>

        {/* Minority Group */}
        <Grid item xs={12} md={12}>
          <div style={{display:'flex', alignItems:'center', gap:'1rem'}}>
            <Typography width={'20%'}>Minority Group : </Typography>
            <Controller
              name="minority_group"
              control={control}
              render={({ field }) => (
                <RadioGroup 
                  row 
                  value={field.value ?? ''} 
                  onChange={(e, value) => field.onChange(value)}
                >
                  <FormControlLabel
                    label='Yes'
                    value={true}
                    control={
                      <Radio disabled={viewMode} />
                    }
                  />
                  <FormControlLabel
                    label='No'
                    value={false}
                    control={
                      <Radio disabled={viewMode} />
                    }
                  />
                  <FormControlLabel
                    label='All'
                    value={'all'}
                    control={
                      <Radio disabled={viewMode} />
                    }
                  />
                </RadioGroup>
              )}
            />
          </div>
        </Grid>

        {/* Student */}
        <Grid item xs={12} md={12}>
          <div style={{display:'flex', alignItems:'center', gap:'1rem'}}>
            <Typography width={'20%'}>Student : </Typography>
            <Controller
              name="student"
              control={control}
              render={({ field }) => (
                <RadioGroup 
                  row 
                  value={field.value ?? ''} 
                  onChange={(e, value) => field.onChange(value)}
                >
                  <FormControlLabel
                    label='Yes'
                    value={true}
                    control={
                      <Radio disabled={viewMode} />
                    }
                  />
                  <FormControlLabel
                    label='No'
                    value={false}
                    control={
                      <Radio disabled={viewMode} />
                    }
                  />
                  <FormControlLabel
                    label='All'
                    value={'all'}
                    control={
                      <Radio disabled={viewMode} />
                    }
                  />
                </RadioGroup>
              )}
            />
          </div>
        </Grid>

        {/* BPL Category */}
        <Grid item xs={12} md={12}>
          <div style={{display:'flex', alignItems:'center', gap:'1rem'}}>
            <Typography width={'20%'}>BPL Category : </Typography>
            <Controller
              name="bpl_category"
              control={control}
              render={({ field }) => (
                <RadioGroup 
                  row 
                  value={field.value ?? ''} 
                  onChange={(e, value) => field.onChange(value)}
                >
                  <FormControlLabel
                    label='Yes'
                    value={true}
                    control={
                      <Radio disabled={viewMode} />
                    }
                  />
                  <FormControlLabel
                    label='No'
                    value={false}
                    control={
                      <Radio disabled={viewMode} />
                    }
                  />
                  <FormControlLabel
                    label='All'
                    value={'all'}
                    control={
                      <Radio disabled={viewMode} />
                    }
                  />
                </RadioGroup>
              )}
            />
          </div>
        </Grid>
      </Grid>
    </Paper>
  );
};

